/**
 * Utility functions for handling tick sizes and price rounding for different futures contracts
 */

// Function to get the appropriate tick size from ticker details
export const getTickSize = (ticker, tickerDetails) => {
  if (!ticker) return 0.01;
  
  // Use PriceFormat.Increment from ticker details if available
  if (tickerDetails?.PriceFormat?.Increment) {
    return tickerDetails.PriceFormat.Increment;
  }
  
  // Fallback to hardcoded values if no ticker details
  const baseTicker = ticker.replace(/[A-Z]\d{2}$/, ''); // Remove month/year suffix
  
  switch (baseTicker) {
    case 'MNQ':
    case 'NQ':
      return 0.25; // Quarter points
    case 'MES':
    case 'ES':
      return 0.25; // Quarter points
    case 'MYM':
    case 'YM':
      return 1; // Whole numbers
    case 'M2K':
    case 'RTY':
      return 0.1; // Tenths
    case 'CL': // Crude Oil
      return 0.01; // Pennies
    case 'GC': // Gold
      return 0.1; // Tenths
    default:
      return 0.01; // Default to 2 decimals for stocks/ETFs
  }
};

// Function to round to the appropriate tick size
export const roundToTickSize = (price, ticker, tickerDetails) => {
  const tickSize = getTickSize(ticker, tickerDetails);
  return Math.round(price / tickSize) * tickSize;
};

// New function to specifically round to two decimal places
export const roundToTwoDecimals = (num) => {
  if (typeof num !== 'number' || isNaN(num)) return 0;
  return parseFloat(num.toFixed(2));
};

/**
 * Decimal places for a tick size (e.g. 0.25 → 2, 1 → 0).
 */
export const getPricePrecision = (tickSize) => {
  const tick = Number(tickSize);
  if (!Number.isFinite(tick) || tick <= 0) return 2;
  const tickStr = String(tick);
  const decimalIndex = tickStr.indexOf('.');
  return decimalIndex === -1 ? 0 : tickStr.length - decimalIndex - 1;
};

/**
 * Chart price-axis / crosshair label: thousand separators + tick precision.
 * Example: 1234.5 → "1,234.50" (tick 0.01) or "1,234.5" (tick 0.1).
 */
export const formatChartAxisPrice = (price, tickSize = 0.01) => {
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  const precision = getPricePrecision(tickSize);
  return n.toLocaleString('en-US', {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

/**
 * Cleans floating-point precision errors by rounding to appropriate decimal places
 * Uses tick size from ticker details to determine decimal places
 */
export const cleanFloatingPointError = (value, ticker = null, tickerDetails = null) => {
  if (value === '' || value === null || value === undefined) return value;
  
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num)) return value;
  
  // Get tick size and calculate decimal places
  const tickSize = getTickSize(ticker, tickerDetails);
  const decimalPlaces = getPricePrecision(tickSize);
  
  return parseFloat(num.toFixed(decimalPlaces));
}; 